import 'yet-another-react-lightbox/styles.css';
import classNames from 'classnames';
import {graphql, HeadProps, PageProps} from 'gatsby';
import {capitalize} from 'lodash';
import React, {useMemo} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Swiper, SwiperRef, SwiperSlide} from 'swiper/react';
import Lightbox from 'yet-another-react-lightbox';

import EnquiryForm from '_components/EnquiryForm';
import Layout from '_components/layout';
import SEO from '_components/SEO';
import SunnyPortableText from '_components/SunnyPortableText';
import chevronLeft from '_images/chevron-left.svg';
import chevronRight from '_images/chevron-right.svg';
import {getAltText, imageUrlBuilder} from '_utils';

export default function Project(props: PageProps<Queries.ProjectDetailQuery>) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const project = props.data.sanityProject;
  const mainSliderRef = useRef<SwiperRef>(null);
  const thumbnailSliderRef = useRef<SwiperRef>(null);
  const [open, setOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);

  useEffect(() => {
    thumbnailSliderRef.current?.swiper.slideTo(selectedIndex);
  }, [selectedIndex]);

  const isMobile = useMemo(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return true;
    }

    return false;
  }, []);

  const thumbnailsPerPage = isMobile ? 3 : 4;

  return (
    <Layout>
      <div className="w-full max-w-section mx-auto px-4 lg:px-20 mt-20">
        <div className="text-28 font-bold">{project?.title}</div>
        <div className="mt-4 text-sm">
          Designed by{' '}
          <span className="font-bold text-dark-blue">
            {project?.partner?.name}
          </span>
        </div>

        <Lightbox
          open={open}
          index={index}
          close={() => setOpen(false)}
          slides={project?.images?.map(img => ({
            alt: getAltText(project?._rawImages, img?._key),
            src: img?.asset ? imageUrlBuilder.image(img).url() : '',
          }))}
        />
        <div className="flex gap-x-20 flex-col lg:flex-row gap-y-10">
          <div className="w-full lg:max-w-[840px] mt-8 flex-1">
            <div>
              <Swiper
                loop={true}
                ref={mainSliderRef}
                onSlideChange={swiper => setSelectedIndex(swiper.realIndex)}>
                {(project?.images || []).map((image, index) => (
                  <SwiperSlide key={index}>
                    {image?.asset ? (
                      <img
                        onClick={() => {
                          setIndex(index);
                          setOpen(true);
                        }}
                        className="object-cover w-full lg:w-[840px] h-[229px] sm:h-[358px] md:h-[400px] lg:h-[458px] rounded-lg cursor-pointer"
                        key={index}
                        src={imageUrlBuilder
                          .image(image)
                          .width(900)
                          .format('webp')
                          .url()}
                        alt={getAltText(project?._rawImages, image?._key)}
                      />
                    ) : (
                      <div />
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="mt-8 flex relative">
                <div className="w-10 lg:w-14 absolute h-full left-0 flex items-center justify-center">
                  <img
                    src={chevronLeft}
                    alt="prev"
                    className="cursor-pointer"
                    onClick={() => {
                      mainSliderRef.current?.swiper.slidePrev();
                    }}
                  />
                </div>
                <div className="w-10 lg:w-14 absolute h-full right-0 flex items-center justify-center">
                  <img
                    src={chevronRight}
                    alt="next"
                    className="cursor-pointer"
                    onClick={() => {
                      mainSliderRef.current?.swiper.slideNext();
                    }}
                  />
                </div>
                <div className="px-10 lg:px-14 w-full">
                  <Swiper
                    slidesPerView={thumbnailsPerPage}
                    slidesPerGroup={thumbnailsPerPage}
                    spaceBetween={isMobile ? 8 : 30}
                    wrapperClass={
                      project?.images &&
                      project?.images.length < thumbnailsPerPage
                        ? classNames('justify-center')
                        : ''
                    }
                    ref={thumbnailSliderRef}>
                    {(project?.images || []).map((image, index) => (
                      <SwiperSlide
                        onClick={() =>
                          mainSliderRef.current?.swiper.slideToLoop(index)
                        }
                        key={index}>
                        {image?.asset ? (
                          <div className="flex justify-center">
                            <img
                              className={classNames(
                                'border border-2 transition-[border] object-cover w-[160px] h-[60px] sm:h-[80px] md:h-[100px] lg:h-[120px] rounded-lg overflow-hidden cursor-pointer',
                                index === selectedIndex
                                  ? 'border-primary'
                                  : 'border-white',
                              )}
                              key={index}
                              src={imageUrlBuilder
                                .image(image)
                                .width(200)
                                .format('webp')
                                .url()}
                              alt={getAltText(project?._rawImages, image?._key)}
                            />
                          </div>
                        ) : (
                          <div />
                        )}
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
            <div className="border-b mt-8"></div>
            <div className="grid grid-cols-3 gap-x-1 lg:gap-x-0 lg:grid-cols-4 gap-y-8 mt-8">
              {[
                {
                  label: 'Property Type',
                  value: project?.propertyType?.name ?? '-',
                },
                {
                  label: 'Status',
                  value: capitalize(project?.status ?? '-'),
                },
                {
                  label: 'Area Size',
                  value: project?.areaSize ? `${project?.areaSize}㎡` : '-',
                },
                {
                  label: 'Cost',
                  value: project?.cost ?? '-',
                },
                {
                  label: 'Bedrooms',
                  value: project?.bedrooms ?? '-',
                },
                {
                  label: 'Style',
                  value: project?.style?.name ?? '-',
                },
                {
                  label: 'Duration',
                  value: project?.duration ?? '-',
                },
                {
                  label: 'Completed',
                  value: project?.completedAt ?? '-',
                },
              ].map(item => (
                <div className="font-medium" key={item.label}>
                  <div className="uppercase text-xxs lg:text-xs opacity-50">
                    {item.label}
                  </div>
                  <div className="mt-2 text-sm lg:text-base">{item.value}</div>
                </div>
              ))}
            </div>
            <div className="mt-16">
              <div className="font-semibold">Project Description</div>
              <div className="mt-4">
                <SunnyPortableText value={project?._rawProjectDescription} />
              </div>
            </div>
            <div className="mt-16">
              <div className="font-semibold">
                More About{' '}
                <span className="text-primary font-bold">
                  {project?.partner?.name}
                </span>
              </div>
              <div className="mt-4">
                <SunnyPortableText value={project?.partner?._rawDescription} />
              </div>
            </div>
          </div>
          <div className="w-full lg:max-w-[360px] p-4 lg:p-8 bg-[#f6f6f6] rounded-2xl">
            <div className="text-lg lg:text-xl font-semibold text-gray1">
              Enquire Now
            </div>
            <div className="mt-8">
              <EnquiryForm includeLink />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
export const Head = (props: HeadProps<Queries.ProjectDetailQuery>) => (
  <>
    <title>{props.data.sanityProject?.title}</title>
    <SEO
      title={
        props.data.sanityProject?.seoTitle || props.data.sanityProject?.title
      }
      description={
        props.data.sanityProject?.seoDescription ||
        props.data.sanityProject?.title
      }
    />
  </>
);

export const query = graphql`
  query ProjectDetail($projectId: String) {
    sanityProject(id: {eq: $projectId}) {
      id
      title
      status
      areaSize
      cost
      bedrooms
      _rawProjectDescription
      duration
      completedAt
      style {
        name
      }
      propertyType {
        name
      }
      partner {
        name
        _rawDescription
      }
      style {
        name
      }
      _rawImages
      images {
        ...ImageInfo
      }
      seoTitle
      seoDescription
    }
  }
`;
